import React, { useState, useEffect } from 'react';
import { MultiplayerRoom, GameCartridge, UserProfile, RoomSettings } from '@/types';
import { databaseService } from '@/services/databaseService';
import { multiplayerStoryService } from '@/services/multiplayerStoryService';
import LoadingIndicator from '@/components/LoadingIndicator';
import { 
  ArrowPathIcon,
  ArrowDownTrayIcon, 
  LockIcon, 
  EyeIcon, 
  XCircleIcon,
  UserIcon,
  UserPlusIcon
} from '@/components/icons';

// Define the structure for room creation parameters
export interface CreateRoomParameters {
  name: string;
  description: string;
  isPublic: boolean;
  maxPlayers: number;
  settings: Partial<RoomSettings>;
}

interface RoomBrowserScreenProps {
  selectedCartridge: GameCartridge;
  userProfile: UserProfile;
  onJoinRoom: (room: MultiplayerRoom) => void;
  onCreateRoom: (params: CreateRoomParameters) => void;
  onBack: () => void;
}

const RoomBrowserScreen: React.FC<RoomBrowserScreenProps> = ({
  selectedCartridge,
  userProfile,
  onJoinRoom,
  onCreateRoom,
  onBack
}) => {
  const [rooms, setRooms] = useState<MultiplayerRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningRoom, setJoiningRoom] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  
  // Create Room Form State
  const [newRoomName, setNewRoomName] = useState(`${userProfile.name}'s Game`);
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [newRoomIsPublic, setNewRoomIsPublic] = useState(true);
  const [newRoomMaxPlayers, setNewRoomMaxPlayers] = useState(selectedCartridge.maxPlayers || 4);

  useEffect(() => {
    loadRooms();
    const interval = setInterval(loadRooms, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, [selectedCartridge.id]);

  const loadRooms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[RoomBrowser] Loading available rooms for cartridge:', selectedCartridge.id);
      const publicRooms = await multiplayerStoryService.getAvailableRooms(selectedCartridge.id);
      console.log('[RoomBrowser] Loaded rooms:', publicRooms);
      setRooms(publicRooms);
    } catch (err) {
      console.error('Error loading rooms:', err);
      setError('Failed to load rooms. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (room: MultiplayerRoom) => {
    if (room.currentPlayers.length >= room.maxPlayers) {
        setError("Cannot join: Room is full.");
        return;
    }
    try {
      setJoiningRoom(room.id);
      setError(null);
      console.log('[RoomBrowser] Joining room:', room.id);
      const userId = userProfile.uid || '';
      const success = await multiplayerStoryService.joinRoom(room.id, userId);
      if (success) {
        onJoinRoom(room);
      } else {
        setError('Failed to join room. It might be full or no longer available.');
      }
    } catch (err) {
      console.error('Error joining room:', err);
      setError(err instanceof Error ? err.message : 'Failed to join room. It might be full or no longer available.');
    } finally {
      setJoiningRoom(null);
    }
  };

  const handleJoinByCode = async () => {
    if (!inviteCode.trim()) {
        setError("Please enter a room code.");
        return;
    }
    try {
      setJoiningRoom('invite');
      setError(null);
      const room = await databaseService.joinRoomByInviteCode(inviteCode.toUpperCase());
      onJoinRoom(room);
    } catch (err) {
      console.error('Error joining by code:', err);
      setError('Invalid room code, room is full, or an error occurred.');
    } finally {
      setJoiningRoom(null);
    }
  };

  const handleCreateRoomSubmit = async () => {
    if (!newRoomName.trim()) {
      setError('Room name is required.');
      return;
    }
    if (newRoomMaxPlayers < 2 || newRoomMaxPlayers > (selectedCartridge.maxPlayers || 8)) {
        setError(`Max players must be between 2 and ${selectedCartridge.maxPlayers || 8}.`);
        return;
    }

    try {
      setIsCreatingRoom(true);
      setError(null);
      
      const params: CreateRoomParameters = {
        name: newRoomName,
        description: newRoomDescription,
        isPublic: newRoomIsPublic,
        maxPlayers: newRoomMaxPlayers,
        settings: {} // Default empty settings, can be expanded later
      };

      // Call the onCreateRoom prop passed from App.tsx
      onCreateRoom(params);
      
      // App.tsx's handleCreateRoom will be responsible for actual room creation 
      // and subsequent navigation/state updates.
      // For now, RoomBrowserScreen assumes creation will be handled and simply closes the form.

    } catch (err) {
      console.error('Error during room creation parameter submission:', err);
      // This error is less likely now as the actual creation is in App.tsx,
      // but kept for safety in case of issues with onCreateRoom callback itself.
      setError(err instanceof Error ? err.message : 'Failed to initiate room creation.');
    } finally {
      setIsCreatingRoom(false);
    }
    
    // Reset form and close, regardless of success/failure of the callback execution
    // as App.tsx is now responsible for feedback on creation.
    setShowCreateForm(false);
    setNewRoomName(`${userProfile.name}'s Game`);
    setNewRoomDescription('');
    setNewRoomIsPublic(true);
    setNewRoomMaxPlayers(selectedCartridge.maxPlayers || 4);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6 lg:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header */} 
        <header className="flex flex-col sm:flex-row justify-between items-center mb-6 md:mb-8 pb-4 border-b border-slate-700">
          <div className="flex items-center mb-4 sm:mb-0">
            <button onClick={onBack} className="mr-3 p-2 rounded-full hover:bg-slate-700 transition-colors" aria-label="Back to Cartridge Selection">
              <ArrowDownTrayIcon className="w-6 h-6 text-slate-300" />
            </button>
            <div>
                <h1 className="text-2xl md:text-3xl font-orbitron text-cyan-400 neon-text-cyan">
                    Multiplayer Rooms
                </h1>
                <p className="text-sm text-slate-400">Cartridge: <span className="font-semibold text-amber-300">{selectedCartridge.name}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold rounded-lg shadow-md transition-all transform hover:scale-105"
            >
              <UserPlusIcon className="w-5 h-5 mr-2" /> Create Room
            </button>
            <button onClick={loadRooms} disabled={isLoading} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-full transition-colors disabled:opacity-50" aria-label="Refresh Room List">
              <ArrowPathIcon className={`w-5 h-5 text-slate-300 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        {/* Error Display */} 
        {error && (
          <div className="mb-6 p-3 bg-red-900/60 border border-red-500/70 rounded-lg text-red-200 text-sm flex items-center">
            <XCircleIcon className="w-5 h-5 mr-2 text-red-300 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Join by Code Section */} 
        <div className="mb-6 p-4 bg-slate-800/60 rounded-lg border border-slate-700">
          <h2 className="text-lg font-semibold text-slate-100 mb-2">Join by Invite Code</h2>
          <div className="flex gap-2">
            <input 
              type="text" 
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Enter Room Code (e.g., ABCD12)"
              className="flex-grow p-2 bg-slate-700 border border-slate-600 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none placeholder-slate-500"
            />
            <button 
              onClick={handleJoinByCode}
              disabled={joiningRoom === 'invite' || !inviteCode.trim()}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-md transition-colors disabled:opacity-60"
            >
              {joiningRoom === 'invite' ? <LoadingIndicator message="" /> : 'Join'}
            </button>
          </div>
        </div> 

        {/* Room List */} 
        {isLoading && rooms.length === 0 ? (
          <LoadingIndicator message="Scanning for active multiplayer dimensions..." />
        ) : rooms.length === 0 ? (
          <div className="text-center py-10 px-6 bg-slate-800/40 rounded-lg border border-slate-700/50">
            <UserIcon className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No Rooms Found</h3>
            <p className="text-slate-400 text-sm mb-4">No active rooms for this cartridge yet.</p>
            <button 
                onClick={() => setShowCreateForm(true)} 
                className="px-6 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold rounded-lg transition-colors"
            >
                Be the First to Host!
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {rooms.map(room => (
              <RoomCard 
                key={room.id} 
                room={room} 
                onJoin={() => handleJoinRoom(room)} 
                isJoining={joiningRoom === room.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Room Modal/Form */} 
      {showCreateForm && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 p-6 rounded-lg shadow-xl w-full max-w-lg border border-slate-700">
            <h3 className="text-2xl font-orbitron text-cyan-400 mb-4">Create New Room</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="roomName" className="block text-sm font-medium text-slate-300">Room Name</label>
                <input type="text" id="roomName" value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)} className="mt-1 block w-full bg-slate-700 border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm" />
              </div>
              <div>
                <label htmlFor="roomDescription" className="block text-sm font-medium text-slate-300">Description (Optional)</label>
                <textarea id="roomDescription" value={newRoomDescription} onChange={(e) => setNewRoomDescription(e.target.value)} rows={2} className="mt-1 block w-full bg-slate-700 border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"></textarea>
              </div>
               <div>
                <label htmlFor="maxPlayers" className="block text-sm font-medium text-slate-300">Max Players ({`2-${selectedCartridge.maxPlayers || 8}`})</label>
                <input type="number" id="maxPlayers" value={newRoomMaxPlayers} onChange={(e) => setNewRoomMaxPlayers(parseInt(e.target.value, 10))} min="2" max={selectedCartridge.maxPlayers || 8} className="mt-1 block w-full bg-slate-700 border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="isPublic" checked={newRoomIsPublic} onChange={(e) => setNewRoomIsPublic(e.target.checked)} className="h-4 w-4 text-cyan-600 border-slate-500 rounded focus:ring-cyan-500" />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-slate-300">Publicly Listed</label>
              </div>
            </div>
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-cyan-500">Cancel</button>
              <button onClick={handleCreateRoomSubmit} disabled={isCreatingRoom} className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-cyan-500 disabled:opacity-50">
                {isCreatingRoom ? <LoadingIndicator message="" /> : 'Create Room'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual room card component
interface RoomCardProps {
  room: MultiplayerRoom;
  onJoin: () => void;
  isJoining: boolean;
}

const RoomCard: React.FC<RoomCardProps> = ({ room, onJoin, isJoining }) => {
  const currentPlayerCount = Array.isArray(room.currentPlayers) ? room.currentPlayers.length : 0;
  const isFull = currentPlayerCount >= room.maxPlayers;
  const canJoin = !isFull;

  return (
    <div className={`bg-slate-800/70 border border-slate-700 rounded-xl p-5 hover:shadow-cyan-500/20 hover:shadow-lg transition-all duration-300 flex flex-col justify-between ${!canJoin && 'opacity-70'}`}>
      <div>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-orbitron text-slate-50 mb-1 truncate w-48" title={room.name}>{room.name}</h3>
            <p className="text-xs text-slate-400">
              Room ID: <span className="font-mono text-cyan-300 tracking-wider">{room.id.substring(0, 8)}</span>
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="flex items-center gap-1.5 mb-1 px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
              Active
            </div>
          </div>
        </div>

        {room.description && (
          <p className="text-sm text-slate-300 mb-3 h-10 overflow-hidden text-ellipsis line-clamp-2" title={room.description}>{room.description}</p>
        )}

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-slate-400 flex items-center"><UserIcon className="w-4 h-4 mr-1.5 text-slate-500"/>Players</span>
            <span className="text-sm font-medium text-slate-200">
              {currentPlayerCount} / {room.maxPlayers}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[22px]">
          {room.isPublic ? 
            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full flex items-center"><EyeIcon className="w-3 h-3 mr-1"/>Public</span> : 
            <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full flex items-center"><LockIcon className="w-3 h-3 mr-1"/>Private</span>
          }
        </div>
      </div>

      <button
        onClick={onJoin}
        disabled={!canJoin || isJoining}
        className={`w-full mt-auto py-2.5 font-semibold rounded-lg transition-all duration-200 text-sm ${ 
          !canJoin
            ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white transform hover:scale-[1.03] focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-800'
        }`}
      >
        {isJoining
          ? <LoadingIndicator message="" />
          : isFull
          ? 'Room Full'
          : 'Join Room'
        }
      </button>
    </div>
  );
};

export default RoomBrowserScreen;
